/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Sản phẩm & giá › Danh mục sản phẩm (ADR-026 §6): product groups as tiles, their products, a
 * product's page (Thông tin · Gói · Tuỳ chọn cấu hình · Add-on · Quá hạn & gia hạn · Lịch sử) and a
 * plan's page (Thông số · Giá theo chu kỳ · Kết nối module · Tồn kho & vị trí · Tên miền tặng · Bán kèm).
 * The open product, plan and tab live in the URL (`product`, `plan`, `view`). The editor's forms
 * (EditorForms.tsx) are offered to who holds the permission: `catalog.manage` to edit,
 * `catalog.publish` to show or hide, `pricing.manage` to set prices.
 */
import { ArrowLeft, CircleDollarSign, CirclePlus, CircleStop, Eye, Pencil, Plus, RotateCcw, Send } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, isApiError } from '../../api';
import type {
  CatalogChange,
  CatalogPlan,
  CatalogProduct,
  LifecycleSettings,
  OptionGroup,
  PlanDetail,
  ProductDetail,
  PublishProblem,
  PublishResult,
  StockMode,
  Visibility,
} from '../../api/areas/catalog';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { cycleDiscount, formatVnd, lifecycleCalendar, lifecycleProblems, parseReminderDays, parseVnd, priceGrid, resolveLifecycle, sortCycles } from '../../lib/catalog';
import { formatDateTime } from '../../lib/format';
import { useAccess } from '../../lib/access';
import { useApi } from '../../lib/useApi';
import {
  AddonForm,
  AddStockForm,
  ClearLifecycleForm,
  CrossSellForm,
  DomainOptionsForm,
  EndPlanPriceForm,
  ExtraPriceForm,
  FreeDomainsForm,
  GroupForm,
  OptionGroupForm,
  OptionItemForm,
  PlanForm,
  ProductForm,
  ProductVisibilityForm,
  takesItems,
} from './EditorForms';
import { useModuleSummaries } from './moduleKit';
import { PlanModuleTab } from './PlanModuleTab';
import { useText } from '../modules/ui';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';

const VIS_TONE: Record<Visibility, Tone> = { DRAFT: 'neutral', PUBLIC: 'ok', HIDDEN: 'warn', RETIRED: 'neutral' };

function useCatalogT() {
  const { t, i18n } = useTranslation('catalog');
  const en = i18n.language.startsWith('en');
  const name = (x: { name_vi: string; name_en: string }) => (en ? x.name_en : x.name_vi);
  return { t, en, name };
}

function VisibilityBadge({ value }: { value: Visibility }) {
  const { t } = useCatalogT();
  return <Badge tone={VIS_TONE[value]}>{t(`visibility.${value}`)}</Badge>;
}

/** Tab buttons of a product or plan page (`view` in the URL). */
function ViewTabs({ tabs, active, onPick, label }: { tabs: readonly { id: string; label: string }[]; active: string; onPick: (id: string) => void; label: string }) {
  return (
    <nav className="mb-5 max-w-full overflow-x-auto" aria-label={label}>
      <div className="inline-flex w-max gap-1 rounded-lg bg-muted p-1">
        {tabs.map((x) => (
          <button
            key={x.id}
            type="button"
            aria-current={x.id === active ? 'page' : undefined}
            onClick={() => onPick(x.id)}
            className={`inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap text-muted-foreground hover:text-foreground ${x.id === active ? 'bg-background text-foreground shadow-xs' : ''}`}
          >
            {x.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

function Problems({ problems }: { problems: readonly PublishProblem[] }) {
  const { t } = useCatalogT();
  if (problems.length === 0) return <Badge tone="ok">{t('plans.ready')}</Badge>;
  return (
    <ul className="grid gap-0.5 text-xs text-muted-foreground">
      {problems.map((p, i) => (
        <li key={`${p.code}-${i}`}>
          {t(`problem.${p.code}`, { defaultValue: t(`fulfil.problem.${p.code}`, { defaultValue: p.code }) })}
          {p.detail ? <span className="text-muted-foreground/80"> · {p.detail}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/** `/products?tab=product-catalog`: groups, products, a product, a plan. */
export function CatalogPage() {
  const { t } = useCatalogT();
  useDocumentTitle(t('title'));
  const [params, setParams] = useSearchParams();
  const product = params.get('product');
  const plan = params.get('plan');
  const go = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next);
  };
  if (plan) return <PlanView id={plan} onBack={() => go({ plan: null, view: null })} />;
  if (product) return <ProductView id={product} onBack={() => go({ product: null, view: null })} onPlan={(id, v) => go({ plan: id, view: v ?? null })} />;
  return <Overview group={params.get('group')} onGroup={(g) => go({ group: g })} onProduct={(id) => go({ product: id, view: null })} />;
}

function Overview({ group, onGroup, onProduct }: { group: string | null; onGroup: (g: string | null) => void; onProduct: (id: string) => void }) {
  const { t, name } = useCatalogT();
  const access = useAccess();
  const manage = access.has(['catalog.manage']);
  const groups = useApi((signal) => api.listCatalogGroups({ signal }), []);
  const products = useApi((signal) => api.listCatalogProducts(group, { signal }), [group]);
  const groupList = groups.status === 'success' ? groups.data.groups : [];
  const current = groupList.find((g) => g.code === group);
  const reload = () => {
    groups.reload();
    products.reload();
  };
  const groupName = (code: string) => {
    const g = groups.status === 'success' ? groups.data.groups.find((x) => x.code === code) : undefined;
    return g ? name(g) : code;
  };
  const columns: Column<CatalogProduct>[] = [
    {
      key: 'product',
      header: t('col.product'),
      cell: (p) => (
        <button type="button" className="text-left font-medium text-primary underline-offset-4 hover:underline" onClick={() => onProduct(p.id)}>
          {name(p)}
          <span className="block font-mono text-xs font-normal text-muted-foreground">{p.code}</span>
        </button>
      ),
    },
    { key: 'group', header: t('col.group'), cell: (p) => groupName(p.group_code) },
    { key: 'delivery', header: t('col.delivery'), cell: (p) => (p.branch ? `${t(`fulfilment.${p.fulfilment}`)} · ${t(`branch.${p.branch}`)}` : t(`fulfilment.${p.fulfilment}`)) },
    { key: 'plans', header: t('col.plans'), cell: (p) => `${t('onSale', { count: p.plans_on_sale ?? 0 })} / ${p.plan_count ?? 0}` },
    { key: 'visibility', header: t('col.visibility'), cell: (p) => <VisibilityBadge value={p.visibility} /> },
  ];
  return (
    <>
      <PageHeader
        title={t('title')}
        crumbs={[{ label: t('crumb') }]}
        subtitle={t('subtitle')}
        actions={
          manage ? (
            <>
              {current ? (
                <ActionDialog label={t('editor.editGroup')} icon={Pencil} title={t('editor.editGroupTitle', { group: name(current) })} wide onChanged={reload}>
                  {(p) => <GroupForm {...p} group={current} />}
                </ActionDialog>
              ) : null}
              <ActionDialog label={t('editor.newGroup')} icon={Plus} title={t('editor.newGroupTitle')} wide onChanged={reload}>
                {(p) => <GroupForm {...p} />}
              </ActionDialog>
              {groupList.length ? (
                <ActionDialog label={t('editor.newProduct')} icon={CirclePlus} title={t('editor.newProductTitle')} variant="default" wide onChanged={reload}>
                  {(p) => <ProductForm {...p} groups={groupList} defaultGroup={group} />}
                </ActionDialog>
              ) : null}
            </>
          ) : null
        }
      />
      <div className="flex flex-col gap-6">
        {groups.status === 'error' ? <DataError error={groups.error} onRetry={groups.reload} /> : null}
        {groups.status === 'success' ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" role="group" aria-label={t('groups')}>
            <GroupTile active={!group} title={t('allGroups')} onClick={() => onGroup(null)} />
            {groups.data.groups.map((g) => (
              <GroupTile key={g.code} active={group === g.code} title={name(g)} onClick={() => onGroup(g.code)}>
                {t('productCount', { count: g.product_count ?? 0 })} · {t('onSale', { count: g.plans_on_sale ?? 0 })} · {t(`saleMode.${g.sale_mode}`)} ·{' '}
                {g.tax_code ? t(`tax.${g.tax_code}`, { defaultValue: g.tax_code }) : t('editor.groupTaxNone')}
              </GroupTile>
            ))}
          </div>
        ) : null}
        <Section flush>
          {products.status === 'loading' ? <Loading rows={4} /> : null}
          {products.status === 'error' ? <DataError error={products.error} onRetry={products.reload} /> : null}
          {products.status === 'success' ? <DataTable columns={columns} rows={products.data.products} rowKey={(p) => p.id} empty={<EmptyState title={t('noProducts')} />} /> : null}
        </Section>
      </div>
    </>
  );
}

function GroupTile({ active, title, children, onClick }: { active: boolean; title: string; children?: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary ${active ? 'border-primary ring-2 ring-primary/20' : ''}`}
    >
      <span className="block text-sm font-semibold">{title}</span>
      {children ? <span className="mt-1 block text-xs text-muted-foreground">{children}</span> : null}
    </button>
  );
}

// ---------------------------------------------------------------------------------------------
// A product

const PRODUCT_VIEWS = ['info', 'plans', 'options', 'addons', 'domains', 'lifecycle', 'history'] as const;

function ProductView({ id, onBack, onPlan }: { id: string; onBack: () => void; onPlan: (id: string, view?: string) => void }) {
  const { t, name } = useCatalogT();
  const [params, setParams] = useSearchParams();
  const view = (PRODUCT_VIEWS as readonly string[]).includes(params.get('view') ?? '') ? (params.get('view') as (typeof PRODUCT_VIEWS)[number]) : 'plans';
  const result = useApi((signal) => api.getCatalogProduct(id, { signal }), [id]);
  if (result.status === 'loading') return <Loading rows={6} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  const d = result.data;
  const pick = (v: string) => {
    const next = new URLSearchParams(params);
    next.set('view', v);
    setParams(next);
  };
  return (
    <>
      <PageHeader
        title={name(d.product)}
        crumbs={[{ label: t('crumb') }, { label: t('title') }]}
        subtitle={<VisibilityBadge value={d.product.visibility} />}
        actions={
          <>
            {d.can.manage ? <ProductEditButton d={d} onChanged={result.reload} /> : null}
            {d.can.publish ? (
              <ActionDialog label={t('editor.productVisibility')} icon={Eye} title={t('editor.productVisibilityTitle', { product: name(d.product) })} onChanged={result.reload}>
                {(p) => <ProductVisibilityForm {...p} product={d.product} />}
              </ActionDialog>
            ) : null}
            <Button type="button" size="sm" variant="outline" onClick={onBack}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              {t('back')}
            </Button>
          </>
        }
      />
      {d.four_eyes ? <Callout tone="info" className="mb-4">{t('fourEyesNotice')}</Callout> : null}
      <ViewTabs label={t('title')} active={view} onPick={pick} tabs={PRODUCT_VIEWS.map((v) => ({ id: v, label: t(`productTabs.${v}`) }))} />
      {view === 'info' ? <ProductInfo d={d} /> : null}
      {view === 'plans' ? <ProductPlans d={d} onPlan={onPlan} onChanged={result.reload} /> : null}
      {view === 'options' ? <ProductOptions d={d} onChanged={result.reload} /> : null}
      {view === 'addons' ? <ProductAddons d={d} onChanged={result.reload} /> : null}
      {view === 'domains' ? <ProductDomains d={d} onPlan={onPlan} onChanged={result.reload} /> : null}
      {view === 'lifecycle' ? <ProductLifecycle d={d} onChanged={result.reload} /> : null}
      {view === 'history' ? <ProductHistory id={d.product.id} /> : null}
    </>
  );
}

/** "Sửa sản phẩm": the groups load with the dialog. */
function ProductEditButton({ d, onChanged }: { d: ProductDetail; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  return (
    <ActionDialog label={t('editor.editProduct')} icon={Pencil} title={t('editor.editProductTitle', { product: name(d.product) })} wide onChanged={onChanged}>
      {(p) => <ProductEditBody {...p} d={d} />}
    </ActionDialog>
  );
}

function ProductEditBody({ d, ...p }: DialogBodyProps & { d: ProductDetail }) {
  const groups = useApi((signal) => api.listCatalogGroups({ signal }), []);
  if (groups.status === 'loading') return <Loading rows={3} />;
  if (groups.status === 'error') return <DataError error={groups.error} onRetry={groups.reload} />;
  return <ProductForm {...p} product={d.product} groups={groups.data.groups} hasPlans={d.plans.length > 0} />;
}

function ProductInfo({ d }: { d: ProductDetail }) {
  const { t, name } = useCatalogT();
  const p = d.product;
  return (
    <Section>
      <KeyValue
        columns={2}
        rows={[
          [t('info.code'), <span key="code" className="font-mono">{p.code}</span>],
          [t('info.group'), d.group ? name(d.group) : p.group_code],
          [t('info.fulfilment'), t(`fulfilment.${p.fulfilment}`)],
          p.branch ? [t('info.branch'), t(`branch.${p.branch}`)] : null,
          [t('info.label'), t(`serviceLabel.${p.service_label}`)],
          [t('info.trial'), p.trial_days ? t('info.trialDays', { count: p.trial_days }) : t('info.none')],
          [t('info.moneyBack'), p.money_back_days ? t('info.trialDays', { count: p.money_back_days }) : t('info.none')],
        ]}
      />
    </Section>
  );
}

function ProductPlans({ d, onPlan, onChanged }: { d: ProductDetail; onPlan: (id: string) => void; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const columns: Column<CatalogPlan>[] = [
    {
      key: 'plan',
      header: t('col.plan'),
      cell: (p) => (
        <button type="button" className="text-left font-medium text-primary underline-offset-4 hover:underline" onClick={() => onPlan(p.id)}>
          {name(p)}
          <span className="block font-mono text-xs font-normal text-muted-foreground">{p.code}</span>
        </button>
      ),
    },
    { key: 'cycles', header: t('col.cycles'), cell: (p) => sortCycles(p.cycles).map((c) => t(`cycle.${c}`)).join(', ') },
    {
      key: 'tax',
      header: t('col.tax'),
      // ADR-032 §2.2: the plan's own code, else its group's ("theo nhóm").
      cell: (p) => {
        const code = p.tax_code ?? d.group?.tax_code ?? null;
        if (!code) return <span className="text-xs text-muted-foreground">{t('plans.noTax')}</span>;
        const label = t(`tax.${code}`, { defaultValue: code });
        return p.tax_code ? label : <>{label} <span className="text-xs text-muted-foreground">· {t('plans.taxFromGroup')}</span></>;
      },
    },
    { key: 'visibility', header: t('col.visibility'), cell: (p) => <VisibilityBadge value={p.visibility} /> },
    { key: 'ready', header: t('col.ready'), cell: (p) => <Problems problems={p.problems ?? []} /> },
    { key: 'actions', header: '', cell: (p) => (d.can.publish ? <PublishButton plan={p} fourEyes={d.four_eyes} onChanged={onChanged} /> : null) },
  ];
  return (
    <Section
      flush
      description={d.plans.some((p) => p.visibility === 'DRAFT') ? t('draftNotice') : undefined}
      title={t('productTabs.plans')}
      actions={
        d.can.manage ? (
          <ActionDialog label={t('plans.newPlan')} icon={CirclePlus} title={t('editor.newPlanTitle', { product: name(d.product) })} wide="xl" onChanged={onChanged}>
            {(p) => <PlanForm {...p} productId={d.product.id} group={d.group} siblings={d.plans} />}
          </ActionDialog>
        ) : null
      }
    >
      <DataTable columns={columns} rows={d.plans} rowKey={(p) => p.id} empty={<EmptyState title={t('plans.empty')} />} />
    </Section>
  );
}

/** "Mở bán" / "Ngừng bán": on production the first click files an approval. */
function PublishButton({ plan, fourEyes, onChanged }: { plan: CatalogPlan; fourEyes: boolean; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const target: Visibility = plan.visibility === 'PUBLIC' ? 'HIDDEN' : 'PUBLIC';
  if (plan.visibility === 'RETIRED') return null;
  const label = target === 'PUBLIC' ? t('plans.publish') : t('plans.unpublish');
  return (
    <ActionDialog label={label} icon={Send} title={t(target === 'PUBLIC' ? 'plans.publishTitle' : 'plans.unpublishTitle', { plan: name(plan) })} description={t('plans.publishDescription')} onChanged={onChanged}>
      {(p) => <PublishForm {...p} plan={plan} target={target} fourEyes={fourEyes} />}
    </ActionDialog>
  );
}

/**
 * On production the first submit files an approval; the form keeps its id, and once another person
 * approved it the same submit carries the change out with it.
 */
function useApprovalFlow() {
  const [approval, setApproval] = useState<string | null>(null);
  const settle = (r: PublishResult | undefined): boolean => {
    if (r?.result === 'APPROVAL_REQUESTED') {
      setApproval(r.approval_request_id);
      return false;
    }
    return Boolean(r);
  };
  return { approval, settle };
}

function PublishForm({ close, changed, plan, target, fourEyes }: DialogBodyProps & { plan: CatalogPlan; target: Visibility; fourEyes: boolean }) {
  const { t } = useCatalogT();
  const [reason, setReason] = useState('');
  const flow = useApprovalFlow();
  // The approval is part of the request: a new Idempotency-Key once it is known.
  // ADR-032 §2.9: 409 FULFILMENT_CHANGED (a server group changed while it was checked): the page reloads on close and checks again.
  const w = useWrite((b: { reason: string; approval: string | null }, opts) =>
    api.publishCatalogPlan(plan.id, { visibility: target, version: plan.version, reason: b.reason, approval_request_id: b.approval }, opts).catch((e: unknown) => {
      if (isApiError(e) && e.code === 'FULFILMENT_CHANGED') changed();
      throw e;
    }),
  );
  const applied = w.state.phase === 'done' && w.state.result.result === 'APPLIED';
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ reason: reason.trim(), approval: flow.approval }).then((r) => flow.settle(r) && changed());
      }}
    >
      {applied ? (
        <Callout tone="ok">{t('applied')}</Callout>
      ) : (
        <>
          {fourEyes ? <Callout tone="info">{flow.approval ? t('approvalRequested') : t('fourEyesNotice')}</Callout> : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter
        busy={w.busy}
        submit={applied ? undefined : flow.approval ? t('applyApproved') : target === 'PUBLIC' ? t('plans.publish') : t('plans.unpublish')}
        onClose={close}
      />
    </form>
  );
}

function ProductOptions({ d, onChanged }: { d: ProductDetail; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const text = useText();
  const cycles = sortCycles(d.plans.flatMap((p) => p.cycles));
  // ADR-032 §2.7: a module's option type and a placement setting named as the module names them.
  const modules = useModuleSummaries(d.product.branch);
  const typeName = (g: OptionGroup) => {
    if (!g.module_type) return t(`options.kind.${g.kind}`);
    const [m, k] = g.module_type.split(':');
    const spec = modules?.find((x) => x.manifest.id === m)?.manifest.option_types.find((o) => o.key === k);
    return spec ? text(spec.label) : g.module_type;
  };
  const targetName = (v: string) => {
    const fd = v.startsWith('setting:') ? modules?.flatMap((x) => x.manifest.placement_settings.fields).find((f) => `setting:${f.key}` === v) : undefined;
    return fd ? text(fd.label) : t(`optionKey.${v}`, { defaultValue: v });
  };
  const add = d.can.manage ? (
    <ActionDialog label={t('editor.newOptionGroup')} icon={CirclePlus} title={t('editor.newOptionGroupTitle', { product: name(d.product) })} wide onChanged={onChanged}>
      {(p) => <OptionGroupForm {...p} productId={d.product.id} branch={d.product.branch} siblings={d.options} plans={d.plans} />}
    </ActionDialog>
  ) : null;
  if (d.options.length === 0) {
    return (
      <Section actions={add}>
        <EmptyState title={t('options.empty')} />
      </Section>
    );
  }
  const groupName = (code: string) => {
    const g = d.options.find((o) => o.code === code);
    return g ? name(g) : code;
  };
  return (
    <div className="grid gap-4">
      {add ? <div className="flex justify-end">{add}</div> : null}
      {d.options.map((g) => (
        <Section
          key={g.id}
          title={name(g)}
          // A module's type says what it does by its name; a standard kind names what it fills.
          description={[typeName(g), g.required ? t('options.required') : null, g.changeable ? t('options.changeable') : null, g.maps_to && !g.module_type ? `${t('options.mapsTo')} ${targetName(g.maps_to)}` : null].filter(Boolean).join(' · ')}
          actions={
            d.can.manage ? (
              <>
                {takesItems(g.kind) ? (
                  <ActionDialog label={t('editor.newItem')} icon={Plus} title={t('editor.newItemTitle', { group: name(g) })} wide onChanged={onChanged}>
                    {(p) => <OptionItemForm {...p} group={g} branch={d.product.branch} />}
                  </ActionDialog>
                ) : null}
                <ActionDialog label={t('editor.editOptionGroup')} icon={Pencil} title={t('editor.editOptionGroupTitle', { group: name(g) })} wide onChanged={onChanged}>
                  {(p) => <OptionGroupForm {...p} productId={d.product.id} branch={d.product.branch} group={g} siblings={d.options} plans={d.plans} />}
                </ActionDialog>
              </>
            ) : null
          }
        >
          {g.kind === 'QUANTITY' ? <p className="mb-2 text-sm text-muted-foreground">{`${g.min_qty ?? 0} – ${g.max_qty ?? 0}`}</p> : null}
          <ul className="grid gap-1 text-sm">
            {g.items.map((i) => {
              const price = d.option_prices.find((r) => r.target_id === i.id && r.effective_until === null);
              return (
                <li key={i.id} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{name(i)}</span>
                  {i.placement_group ? <span className="text-xs text-muted-foreground">{t('options.toGroup', { group: i.placement_group })}</span> : null}
                  {i.is_default ? <Badge tone="info">{t('options.default')}</Badge> : null}
                  {i.visibility !== 'PUBLIC' ? <Badge tone="neutral">{t(`editor.itemVisibility.${i.visibility}`)}</Badge> : null}
                  <span className="text-xs text-muted-foreground">
                    {price
                      ? [price.percent ? t('options.percent', { pct: Number(price.percent) }) : null, Number(price.amount) > 0 ? t('options.price', { amount: formatVnd(price.amount), cycle: t(`cycle.${price.cycle}`) }) : null, Number(price.setup_fee) > 0 ? t('options.setup', { amount: formatVnd(price.setup_fee) }) : null]
                          .filter(Boolean)
                          .join(' · ') || t('options.free')
                      : t('options.free')}
                  </span>
                  {d.can.manage ? (
                    <ActionDialog label={t('editor.editItem')} icon={Pencil} variant="ghost" title={t('editor.editItemTitle', { item: name(i) })} wide onChanged={onChanged}>
                      {(p) => <OptionItemForm {...p} group={g} item={i} branch={d.product.branch} />}
                    </ActionDialog>
                  ) : null}
                  {d.can.price && cycles.length ? (
                    <ActionDialog label={t('editor.setPrice')} icon={CircleDollarSign} variant="ghost" title={t('editor.optionPriceTitle', { item: name(i) })} wide="xl" onChanged={onChanged}>
                      {(p) => <ExtraPriceForm {...p} kind="option" target={i.id} cycles={cycles} books={d.price_books} current={d.option_prices.filter((r) => r.target_id === i.id)} quantity={g.kind === 'QUANTITY'} />}
                    </ActionDialog>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {g.conditions.map((c, k) => {
            const trigger = d.options.find((o) => o.code === c.when_group);
            const itemName = (grp: typeof g | undefined, code: string) => {
              const it = grp?.items.find((x) => x.code === code);
              return it ? name(it) : code;
            };
            return (
              <p key={k} className="mt-2 text-xs text-muted-foreground">
                {t('editor.conditionSentence', {
                  group: groupName(c.when_group),
                  items: c.when_items.map((x) => itemName(trigger, x)).join(', '),
                  effect: t(`options.effect.${c.effect}`),
                  targets: c.items.map((x) => itemName(g, x)).join(', ') || t('editor.anyItem'),
                })}
              </p>
            );
          })}
        </Section>
      ))}
    </div>
  );
}

function ProductAddons({ d, onChanged }: { d: ProductDetail; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  // ADR-032 §4.1: an add-on has no hourly price (…2020 leaves addon_prices without H1).
  const cycles = sortCycles(d.plans.flatMap((p) => p.cycles)).filter((c) => c !== 'H1');
  const add = d.can.manage ? (
    <ActionDialog label={t('editor.newAddon')} icon={CirclePlus} title={t('editor.newAddonTitle')} wide onChanged={onChanged}>
      {(p) => <AddonForm {...p} defaultProduct={d.product.id} />}
    </ActionDialog>
  ) : null;
  if (d.addons.length === 0) {
    return (
      <Section actions={add}>
        <EmptyState title={t('addons.empty')} />
      </Section>
    );
  }
  return (
    <Section flush actions={add} title={t('productTabs.addons')}>
      <DataTable
        columns={[
          { key: 'name', header: t('col.product'), cell: (a) => <span className="font-medium">{name(a)}</span> },
          { key: 'billing', header: t('col.cycles'), cell: (a) => t(`addons.billing.${a.billing}`) },
          { key: 'tax', header: t('col.tax'), cell: (a) => t(`tax.${a.tax_code}`, { defaultValue: a.tax_code }) },
          {
            key: 'price',
            header: t('col.fromPrice'),
            cell: (a) => {
              const p = d.addon_prices.find((r) => r.target_id === a.id && r.effective_until === null);
              return p ? `${formatVnd(p.amount)} đ / ${t(`cycle.${p.cycle}`)}` : <span className="text-muted-foreground">{t('addons.noPrice')}</span>;
            },
          },
          { key: 'vis', header: t('col.visibility'), cell: (a) => <VisibilityBadge value={a.visibility} /> },
          {
            key: 'actions',
            header: '',
            cell: (a) => (
              <span className="inline-flex flex-wrap gap-1">
                {d.can.manage ? (
                  <ActionDialog label={t('editor.editAddon')} icon={Pencil} variant="ghost" title={t('editor.editAddonTitle', { addon: name(a) })} wide onChanged={onChanged}>
                    {(p) => <AddonForm {...p} addon={a} />}
                  </ActionDialog>
                ) : null}
                {d.can.price && cycles.length ? (
                  <ActionDialog label={t('editor.setPrice')} icon={CircleDollarSign} variant="ghost" title={t('editor.addonPriceTitle', { addon: name(a) })} wide onChanged={onChanged}>
                    {(p) => <ExtraPriceForm {...p} kind="addon" target={a.id} cycles={a.billing === 'ONCE' ? ['ONCE'] : cycles.filter((c) => c !== 'ONCE')} books={d.price_books} current={d.addon_prices.filter((r) => r.target_id === a.id)} />}
                  </ActionDialog>
                ) : null}
              </span>
            ),
          },
        ]}
        rows={d.addons}
        rowKey={(a) => a.id}
      />
    </Section>
  );
}

/** "Tên miền" (ADR-032 §2.13): what names a sold service, the domain options and each plan's free domains. */
function ProductDomains({ d, onPlan, onChanged }: { d: ProductDetail; onPlan: (id: string, view?: string) => void; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const o = d.product.domain_options;
  const onOff = (v: boolean) => (v ? t('lifecycle.on') : t('lifecycle.off'));
  return (
    <div className="grid gap-4">
      <Section
        title={t('productTabs.domains')}
        description={t('domainOptions.intro')}
        actions={
          d.can.manage ? (
            <ActionDialog label={t('domainOptions.edit')} icon={Pencil} title={t('domainOptions.editTitle', { product: name(d.product) })} wide onChanged={onChanged}>
              {(p) => <DomainOptionsForm {...p} product={d.product} />}
            </ActionDialog>
          ) : null
        }
      >
        <KeyValue
          columns={2}
          rows={[
            [t('info.label'), t(`serviceLabel.${d.product.service_label}`)],
            [t('domainOptions.own'), o.own ? (o.own_registered_here_only ? t('domainOptions.ownHere') : t('domainOptions.ownAny')) : onOff(false)],
            [t('domainOptions.register'), onOff(o.register)],
            [t('domainOptions.transfer'), onOff(o.transfer)],
            [t('domainOptions.tlds'), o.tlds.length ? o.tlds.map((x) => `.${x}`).join(', ') : t('domainOptions.allTlds')],
            [t('domainOptions.subdomain'), o.subdomain ?? t('info.none')],
          ]}
        />
      </Section>
      <Section title={t('domainOptions.free')} description={t('domainOptions.freeIntro')}>
        {d.plans.length === 0 ? (
          <EmptyState title={t('plans.empty')} />
        ) : (
          <ul className="grid gap-1 text-sm">
            {d.plans.map((p) => (
              <li key={p.id}>
                <button type="button" className="text-primary underline-offset-4 hover:underline" onClick={() => onPlan(p.id, 'domains')}>
                  {name(p)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function days(t: ReturnType<typeof useCatalogT>['t'], day: number): string {
  if (day < 0) return t('lifecycle.daysBefore', { count: -day });
  if (day === 0) return t('lifecycle.dueDay');
  return t('lifecycle.daysOverdue', { count: day });
}

function ProductLifecycle({ d, onChanged }: { d: ProductDetail; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const e = d.lifecycle.effective;
  const from = (k: keyof LifecycleSettings) => {
    const s = e.sources[k];
    return s ? <span className="text-xs text-muted-foreground"> · {t('lifecycle.from', { source: t(`lifecycle.source.${s}`) })}</span> : null;
  };
  return (
    <div className="grid gap-4">
      <Section
        title={t('lifecycle.title')}
        description={t('lifecycle.intro')}
        actions={
          d.can.manage ? (
            <>
              {d.lifecycle.own ? (
                <ActionDialog label={t('editor.clearLifecycle')} icon={RotateCcw} title={t('editor.clearLifecycleTitle', { product: name(d.product) })} onChanged={onChanged}>
                  {(p) => <ClearLifecycleForm {...p} product={d.product} />}
                </ActionDialog>
              ) : null}
              <ActionDialog label={t('lifecycle.edit')} icon={Pencil} title={t('lifecycle.editTitle', { product: name(d.product) })} wide onChanged={onChanged}>
                {(p) => <LifecycleForm {...p} d={d} />}
              </ActionDialog>
            </>
          ) : null
        }
      >
        <KeyValue
          columns={2}
          rows={[
            [t('lifecycle.invoice_days_before'), <>{days(t, -e.invoice_days_before)}{from('invoice_days_before')}</>],
            [t('lifecycle.reminder_days'), <>{e.reminder_days.map((x) => days(t, -x)).join('; ')}{from('reminder_days')}</>],
            [t('lifecycle.payment_due_days_before'), <>{days(t, -e.payment_due_days_before)}{from('payment_due_days_before')}</>],
            [t('lifecycle.suspend'), <>{e.suspend_enabled ? days(t, e.suspend_after_days) : t('lifecycle.off')}{from('suspend_after_days')}</>],
            [t('lifecycle.terminate'), <>{e.terminate_enabled ? days(t, e.terminate_after_days) : t('lifecycle.off')}{from('terminate_after_days')}</>],
            [t('lifecycle.keep_backups_days'), <>{t('lifecycle.days', { count: e.keep_backups_days })}{from('keep_backups_days')}</>],
            [t('lifecycle.late_fee'), <>{e.late_fee_percent ? t('lifecycle.lateFee', { pct: e.late_fee_percent, days: e.late_fee_after_days }) : t('lifecycle.noLateFee')}{from('late_fee_percent')}</>],
            [t('lifecycle.auto_debit_wallet'), <>{e.auto_debit_wallet ? t('lifecycle.on') : t('lifecycle.off')}{from('auto_debit_wallet')}</>],
          ]}
        />
      </Section>
      <Section title={t('lifecycle.calendar')} flush>
        <DataTable
          columns={[
            { key: 'day', header: t('lifecycle.day'), className: 'whitespace-nowrap', cell: (r) => days(t, r.day) },
            { key: 'event', header: t('history.what'), cell: (r) => t(`lifecycle.event.${r.event}`) },
          ]}
          rows={d.lifecycle.calendar}
          rowKey={(r) => `${r.day}-${r.event}`}
        />
      </Section>
    </div>
  );
}

const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

function LifecycleForm({ close, changed, d }: DialogBodyProps & { d: ProductDetail }) {
  const { t } = useCatalogT();
  const own = d.lifecycle.own;
  const [invoice, setInvoice] = useState(own?.invoice_days_before?.toString() ?? '');
  const [reminders, setReminders] = useState(own?.reminder_days?.join(', ') ?? '');
  const [suspend, setSuspend] = useState(own?.suspend_after_days?.toString() ?? '');
  const [terminate, setTerminate] = useState(own?.terminate_after_days?.toString() ?? '');
  const [reason, setReason] = useState('');
  const parsedReminders = reminders.trim() ? parseReminderDays(reminders) : null;
  const settings: Partial<LifecycleSettings> = {
    ...(own ?? {}),
    invoice_days_before: num(invoice),
    reminder_days: reminders.trim() ? parsedReminders : null,
    suspend_after_days: num(suspend),
    terminate_after_days: num(terminate),
  };
  const global = { ...d.lifecycle.effective, late_fee_percent: d.lifecycle.effective.late_fee_percent, sources: undefined } as unknown as LifecycleSettings;
  const preview = resolveLifecycle(settings, d.lifecycle.group, global);
  const problems = lifecycleProblems(preview);
  const w = useWrite((b: { settings: Partial<LifecycleSettings>; reason: string }, opts) =>
    api.setLifecycle({ scope: 'PRODUCT', scope_ref: d.product.id, version: d.lifecycle.own_version, settings: b.settings, reason: b.reason }, opts),
  );
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ settings, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{t('applied')}</Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('lifecycle.emptyInherits')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('lifecycle.invoice_days_before')}>
              <Input type="number" min={0} max={90} value={invoice} onChange={(e) => setInvoice(e.target.value)} placeholder={String(d.lifecycle.effective.invoice_days_before)} />
            </Field>
            <Field label={t('lifecycle.reminder_days')} hint={t('lifecycle.reminderHint')}>
              <Input value={reminders} onChange={(e) => setReminders(e.target.value)} placeholder={d.lifecycle.effective.reminder_days.join(', ')} />
            </Field>
            <Field label={t('lifecycle.suspend')}>
              <Input type="number" min={0} max={365} value={suspend} onChange={(e) => setSuspend(e.target.value)} placeholder={String(d.lifecycle.effective.suspend_after_days)} />
            </Field>
            <Field label={t('lifecycle.terminate')}>
              <Input type="number" min={1} max={730} value={terminate} onChange={(e) => setTerminate(e.target.value)} placeholder={String(d.lifecycle.effective.terminate_after_days)} />
            </Field>
          </div>
          {problems.length ? (
            <Callout tone="warn">
              {problems.map((p) => (
                <p key={p}>{t(`lifecycle.problem.${p}`)}</p>
              ))}
            </Callout>
          ) : (
            <ul className="grid gap-0.5 text-xs text-muted-foreground">
              {lifecycleCalendar(preview).map((r) => (
                <li key={`${r.day}-${r.event}`}>
                  {days(t, r.day)}: {t(`lifecycle.event.${r.event}`)}
                </li>
              ))}
            </ul>
          )}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={problems.length > 0 || (reminders.trim() !== '' && parsedReminders === null)} submit={w.state.phase === 'done' ? undefined : t('save')} onClose={close} />
    </form>
  );
}

function ProductHistory({ id }: { id: string }) {
  const { t } = useCatalogT();
  const result = useApi((signal) => api.getCatalogProductHistory(id, { signal }), [id]);
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  const columns: Column<CatalogChange>[] = [
    { key: 'when', header: t('history.when'), className: 'whitespace-nowrap text-xs', cell: (c) => formatDateTime(c.at) },
    { key: 'what', header: t('history.what'), cell: (c) => <span className="font-mono text-xs">{c.entity}</span> },
    { key: 'why', header: t('history.why'), cell: (c) => c.reason },
    { key: 'who', header: t('history.who'), cell: (c) => (c.actor_id ? <span className="font-mono text-xs">{c.actor_id.slice(0, 8)}</span> : t('history.system')) },
  ];
  return (
    <Section flush>
      <DataTable columns={columns} rows={result.data.changes} rowKey={(c) => c.id} empty={<EmptyState title={t('history.empty')} />} />
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------
// A plan

const PLAN_VIEWS = ['specs', 'prices', 'delivery', 'stock', 'domains', 'alongside'] as const;

function PlanView({ id, onBack }: { id: string; onBack: () => void }) {
  const { t, name } = useCatalogT();
  const [params, setParams] = useSearchParams();
  const view = (PLAN_VIEWS as readonly string[]).includes(params.get('view') ?? '') ? (params.get('view') as (typeof PLAN_VIEWS)[number]) : 'prices';
  const result = useApi((signal) => api.getCatalogPlan(id, { signal }), [id]);
  if (result.status === 'loading') return <Loading rows={6} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  const d = result.data;
  const pick = (v: string) => {
    const next = new URLSearchParams(params);
    next.set('view', v);
    setParams(next);
  };
  return (
    <>
      <PageHeader
        title={`${name(d.product)} · ${name(d.plan)}`}
        crumbs={[{ label: t('crumb') }, { label: t('title') }]}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <VisibilityBadge value={d.plan.visibility} />
            <span className="font-mono text-xs">{d.plan.code}</span>
          </span>
        }
        actions={
          <>
            {d.can.manage ? (
              <ActionDialog label={t('editor.editPlan')} icon={Pencil} title={t('editor.editPlanTitle', { plan: name(d.plan) })} wide="xl" onChanged={result.reload}>
                {(p) => <PlanForm {...p} productId={d.product.id} group={d.group} plan={d.plan} siblings={d.product_plans} />}
              </ActionDialog>
            ) : null}
            {d.can.publish ? <PublishButton plan={d.plan} fourEyes={d.four_eyes} onChanged={result.reload} /> : null}
            <Button type="button" size="sm" variant="outline" onClick={onBack}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              {t('backToProduct')}
            </Button>
          </>
        }
      />
      {d.problems.length ? (
        <Callout tone="warn" title={t('plans.notReady')} className="mb-4">
          <Problems problems={d.problems} />
        </Callout>
      ) : null}
      <ViewTabs label={name(d.plan)} active={view} onPick={pick} tabs={PLAN_VIEWS.map((v) => ({ id: v, label: t(v === 'prices' && d.group.sale_mode === 'HOURLY' ? 'planTabs.pricesHourly' : `planTabs.${v}`) }))} />
      {view === 'specs' ? <PlanSpecs d={d} /> : null}
      {view === 'prices' ? <PlanPrices d={d} onChanged={result.reload} /> : null}
      {view === 'delivery' ? <PlanModuleTab d={d} onChanged={result.reload} /> : null}
      {view === 'stock' ? <PlanStock d={d} onChanged={result.reload} /> : null}
      {view === 'domains' ? <PlanDomains d={d} onChanged={result.reload} /> : null}
      {view === 'alongside' ? <PlanAlongside d={d} onChanged={result.reload} /> : null}
    </>
  );
}

function PlanSpecs({ d }: { d: PlanDetail }) {
  const { t, en } = useCatalogT();
  if (d.plan.specs.length === 0) return <Section><EmptyState title={t('specs.empty')} /></Section>;
  return (
    <Section>
      <KeyValue columns={2} rows={d.plan.specs.map((s) => [en ? s.label_en : s.label_vi, `${s.value}${s.unit ? ` ${s.unit}` : ''}`] as const)} />
    </Section>
  );
}

function PlanPrices({ d, onChanged }: { d: PlanDetail; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const cycles = sortCycles(d.plan.cycles);
  const now = new Date().toISOString();
  const grid = priceGrid(d.prices, d.price_books, cycles, now);
  const hourly = d.group.sale_mode === 'HOURLY';
  return (
    <Section
      title={t(hourly ? 'planTabs.pricesHourly' : 'planTabs.prices')}
      description={t(hourly ? 'prices.introHourly' : 'prices.intro')}
      actions={
        d.can.price ? (
          <>
            {d.prices.some((r) => r.effective_until === null) ? (
              <ActionDialog label={t('editor.endPrice')} icon={CircleStop} title={t('editor.endPriceTitle', { plan: name(d.plan) })} onChanged={onChanged}>
                {(p) => <EndPlanPriceForm {...p} d={d} />}
              </ActionDialog>
            ) : null}
            <ActionDialog label={t('prices.edit')} icon={CirclePlus} title={t('prices.editTitle', { plan: d.plan.code })} wide="xl" onChanged={onChanged}>
              {(p) => <PriceForm {...p} d={d} />}
            </ActionDialog>
          </>
        ) : null
      }
      flush
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              <th className="px-4 py-2">{t('prices.book')}</th>
              {cycles.map((c) => (
                <th key={c} className="px-4 py-2 text-right">
                  {c === 'H1' ? t('prices.hourly') : t(`cycle.${c}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.price_books.map((b) => {
              const row = grid.get(b.id);
              const monthly = row?.get('M1')?.current?.amount;
              return (
                <tr key={b.id} className="border-t align-top">
                  <th scope="row" className="px-4 py-2 text-left font-medium">
                    {b.code}
                    <span className="block text-xs font-normal text-muted-foreground">{t(`prices.bookKind.${b.applies_to}`)}</span>
                  </th>
                  {cycles.map((c) => {
                    const cell = row?.get(c);
                    const pct = cell?.current ? cycleDiscount(monthly, cell.current.amount, c) : null;
                    return (
                      <td key={c} className="px-4 py-2 text-right tabular">
                        {cell?.current ? (
                          <>
                            <span className="font-medium">{formatVnd(cell.current.amount)}</span>
                            {pct ? <span className="ml-1 text-xs text-success">{t('discount', { pct })}</span> : null}
                            {Number(cell.current.setup_fee) > 0 ? <span className="block text-xs text-muted-foreground">{t('prices.setup', { amount: formatVnd(cell.current.setup_fee) })}</span> : null}
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('prices.none')}</span>
                        )}
                        {cell?.next ? <span className="block text-xs text-info">{t('prices.next', { date: formatDateTime(cell.next.effective_from), amount: formatVnd(cell.next.amount) })}</span> : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function PriceForm({ close, changed, d }: DialogBodyProps & { d: PlanDetail }) {
  const { t } = useCatalogT();
  const cycles = sortCycles(d.plan.cycles);
  const [book, setBook] = useState(d.price_books[0]?.id ?? '');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [setups, setSetups] = useState<Record<string, string>>({});
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const flow = useApprovalFlow();
  const rows = cycles
    .filter((c) => (amounts[c] ?? '').trim() !== '')
    .map((c) => ({ cycle: c, amount: parseVnd(amounts[c] ?? ''), setup_fee: (setups[c] ?? '').trim() ? parseVnd(setups[c] ?? '') : '0' }));
  const unreadable = rows.some((r) => r.amount === null || r.setup_fee === null);
  type Body = Parameters<typeof api.publishPlanPrices>[1];
  // The whole request (prices, book, start, approval) keys the write: an edit is a new request.
  const w = useWrite((b: Body, opts) => api.publishPlanPrices(d.plan.id, b, opts));
  const body = (): Body => ({
    price_book_id: book,
    prices: rows.map((r) => ({ cycle: r.cycle, amount: r.amount ?? '0', setup_fee: r.setup_fee ?? '0' })),
    effective_from: from ? new Date(from).toISOString() : null,
    reason: reason.trim(),
    approval_request_id: flow.approval,
  });
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run(body()).then((r) => flow.settle(r) && changed());
      }}
    >
      {w.state.phase === 'done' && w.state.result.result === 'APPLIED' ? (
        <Callout tone="ok">{t('applied')}</Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t(d.group.sale_mode === 'HOURLY' ? 'prices.introHourly' : 'prices.intro')}</p>
          {d.four_eyes ? <Callout tone="info">{flow.approval ? t('approvalRequested') : t('fourEyesNotice')}</Callout> : null}
          <Field label={t('prices.book')}>
            <select className={selectClass} value={book} onChange={(e) => setBook(e.target.value)}>
              {d.price_books.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} · {t(`prices.bookKind.${b.applies_to}`)}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid gap-2">
            {cycles.map((c) => (
              <div key={c} className={`grid items-end gap-2 ${c === 'H1' ? 'grid-cols-[8rem_1fr]' : 'grid-cols-[8rem_1fr_1fr]'}`}>
                <span className="pb-2 text-sm font-medium">{c === 'H1' ? t('prices.hourly') : t(`cycle.${c}`)}</span>
                <Field label={c === 'H1' ? t('prices.hourlyAmount') : t('prices.amount')}>
                  <Input inputMode="numeric" value={amounts[c] ?? ''} onChange={(e) => setAmounts({ ...amounts, [c]: e.target.value })} />
                </Field>
                {/* ADR-032 §2.3: an hourly price has no setup fee; the row sends 0. */}
                {c === 'H1' ? null : (
                  <Field label={t('prices.setupFee')}>
                    <Input inputMode="numeric" value={setups[c] ?? ''} onChange={(e) => setSetups({ ...setups, [c]: e.target.value })} placeholder="0" />
                  </Field>
                )}
              </div>
            ))}
          </div>
          {unreadable ? <Callout tone="warn">{t('prices.unreadable')}</Callout> : null}
          <Field label={t('prices.from')} hint={t('prices.fromHint')}>
            <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter
        busy={w.busy}
        disabled={rows.length === 0 || unreadable}
        submit={w.state.phase === 'done' && w.state.result.result === 'APPLIED' ? undefined : flow.approval ? t('applyApproved') : t('save')}
        onClose={close}
      />
    </form>
  );
}

const STOCK_TONE: Record<StockMode, Tone> = { UNLIMITED: 'ok', COUNTED: 'info', CAPACITY: 'info', CLOSED: 'warn' };

function PlanStock({ d, onChanged }: { d: PlanDetail; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const locationName = (code: string) => {
    const l = d.locations.find((x) => x.code === code);
    return l ? `${name(l)} · ${l.city}` : code;
  };
  return (
    <Section
      title={t('planTabs.stock')}
      flush
      actions={
        d.can.manage && d.locations.some((l) => l.active && !d.stock.some((s) => s.location_code === l.code)) ? (
          <ActionDialog label={t('editor.addStock')} icon={Plus} title={t('editor.addStockTitle', { plan: name(d.plan) })} onChanged={onChanged}>
            {(p) => <AddStockForm {...p} d={d} />}
          </ActionDialog>
        ) : null
      }
    >
      <DataTable
        columns={[
          { key: 'loc', header: t('stock.location'), cell: (s) => locationName(s.location_code) },
          {
            key: 'mode',
            header: t('col.visibility'),
            cell: (s) => (
              <span className="inline-flex flex-wrap items-center gap-2">
                <Badge tone={STOCK_TONE[s.mode]}>{t(`stock.mode.${s.mode}`)}</Badge>
                {s.mode === 'COUNTED' ? <span className="text-xs text-muted-foreground">{t('stock.left', { left: (s.quantity ?? 0) - s.reserved, quantity: s.quantity ?? 0 })}</span> : null}
                {s.customer_choice ? <span className="text-xs text-muted-foreground">{t('stock.customerChoice')}</span> : null}
              </span>
            ),
          },
          {
            key: 'edit',
            header: '',
            cell: (s) =>
              d.can.manage ? (
                <ActionDialog label={t('stock.edit')} icon={Pencil} title={t('stock.editTitle', { plan: d.plan.code, location: locationName(s.location_code) })} onChanged={onChanged}>
                  {(p) => <StockForm {...p} plan={d.plan.id} location={s.location_code} version={s.version} mode={s.mode} quantity={s.quantity} />}
                </ActionDialog>
              ) : null,
          },
        ]}
        rows={d.stock}
        rowKey={(s) => s.location_code}
        empty={<EmptyState title={t('stock.none')} />}
      />
    </Section>
  );
}

function StockForm({ close, changed, plan, location, version, mode: initial, quantity }: DialogBodyProps & { plan: string; location: string; version: number | null; mode: StockMode; quantity: number | null }) {
  const { t } = useCatalogT();
  const [mode, setMode] = useState<StockMode>(initial);
  const [qty, setQty] = useState(quantity?.toString() ?? '');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { reason: string }, opts) =>
    api.setPlanStock(plan, location, { version, mode, quantity: mode === 'COUNTED' ? Number(qty || '0') : null, reason: b.reason }, opts),
  );
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{t('applied')}</Callout>
      ) : (
        <>
          <Field label={t('col.visibility')}>
            <select className={selectClass} value={mode} onChange={(e) => setMode(e.target.value as StockMode)}>
              {(['UNLIMITED', 'COUNTED', 'CAPACITY', 'CLOSED'] as const).map((m) => (
                <option key={m} value={m}>
                  {t(`stock.mode.${m}`)}
                </option>
              ))}
            </select>
          </Field>
          {mode === 'COUNTED' ? (
            <Field label={t('stock.quantity')}>
              <Input type="number" min={0} value={qty} onChange={(e) => setQty(e.target.value)} className="w-32" />
            </Field>
          ) : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} submit={w.state.phase === 'done' ? undefined : t('save')} onClose={close} />
    </form>
  );
}

function PlanDomains({ d, onChanged }: { d: PlanDetail; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const edit = d.can.manage ? (
    <ActionDialog label={t('editor.editFreeDomains')} icon={Pencil} title={t('editor.freeDomainsTitle', { plan: name(d.plan) })} wide onChanged={onChanged}>
      {(p) => <FreeDomainsForm {...p} d={d} />}
    </ActionDialog>
  ) : null;
  return (
    <Section actions={edit}>
      {d.free_domains.length === 0 ? (
        <EmptyState title={t('domains.none')} />
      ) : (
        <ul className="grid gap-1 text-sm">
          {d.free_domains.map((f) => (
            <li key={f.tld_id}>
              {t('domains.rule', { zone: f.zone ?? f.tld_id, cycle: t(`cycle.${f.min_cycle}`) })} · {f.operations.map((o) => t(`editor.freeOperation.${o}`)).join(', ')} · {f.free_renewals ? t('domains.renewals') : t('domains.firstTerm')}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function PlanAlongside({ d, onChanged }: { d: PlanDetail; onChanged: () => void }) {
  const { t, en, name } = useCatalogT();
  const edit = d.can.manage ? (
    <ActionDialog label={t('editor.editCrossSells')} icon={Pencil} title={t('editor.crossSellsTitle', { plan: name(d.plan) })} wide onChanged={onChanged}>
      {(p) => <CrossSellForm {...p} d={d} />}
    </ActionDialog>
  ) : null;
  return (
    <Section description={t('alongside.intro')} actions={edit}>
      {d.cross_sells.length === 0 ? (
        <EmptyState title={t('alongside.none')} />
      ) : (
        <ul className="grid gap-1 text-sm">
          {d.cross_sells.map((c) => (
            <li key={c.offered_plan_id}>
              <span className="font-medium">{(en ? c.name_en : c.name_vi) ?? c.code}</span> <span className="font-mono text-xs text-muted-foreground">{c.code}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
